import { useCallback } from 'react';

import { GeocodeFragment } from '@graphql/platform';

import { WWW_ROUTES } from '@root/root/routes';

import { ServiceSelection } from '@shared/service_selector/constants';

const REFERRER_KEY = 'ref';
const ZIP_KEY = 'zip';
const ZIP_VALIDATED_KEY = 'zv';
const ZIP_VALIDATED_VALUE = 't';

/**
 * Builds checkout query string, optionally merging params from the current page
 * before applying zip / `zv`.
 */
export const buildCheckoutQueryString = ({
  zip,
  zipValidated,
  preserveSearch,
}: {
  zip?: string;
  zipValidated?: boolean;
  /** Current `location.search` or `window.location.search` to preserve non-zip params */
  preserveSearch?: string;
}) => {
  const normalized = (preserveSearch ?? '').startsWith('?')
    ? (preserveSearch ?? '').slice(1)
    : (preserveSearch ?? '');
  const params = new URLSearchParams(normalized);

  if (zip) {
    params.set(ZIP_KEY, zip);
  } else {
    params.delete(ZIP_KEY);
  }

  if (zipValidated === true) {
    params.set(ZIP_VALIDATED_KEY, ZIP_VALIDATED_VALUE);
  } else {
    params.delete(ZIP_VALIDATED_KEY);
  }

  return params.toString();
};

export const parseCheckoutQueryString = (query: string) => {
  const normalized = query.startsWith('?') ? query.slice(1) : query;
  const params = new URLSearchParams(normalized);
  return {
    zip: params.get(ZIP_KEY) ?? undefined,
    zipValidated: params.get(ZIP_VALIDATED_KEY) == ZIP_VALIDATED_VALUE,
    referrer: params.get(REFERRER_KEY) ?? undefined,
  };
};

export function useResolveCheckoutURL() {
  return useCallback(
    async (
      serviceSelection = ServiceSelection.Storage,
      geocode?: GeocodeFragment,
    ): Promise<string> => {
      const preserveSearch =
        typeof window !== 'undefined' ? window.location.search : '';

      const queryString = buildCheckoutQueryString({
        zip: geocode?.id,
        zipValidated: !!geocode?.id,
        preserveSearch,
      });

      if (serviceSelection === ServiceSelection.Moving) {
        return `${WWW_ROUTES.PRODUCT_PAGE_MOVING}?${queryString}`;
      } else {
        return `${WWW_ROUTES.PRODUCT_PAGE_STORAGE}?${queryString}`;
      }
    },
    [],
  );
}
